import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, AlertCircle, Upload, Clock, CreditCard, Mail, Eye,
  ChevronRight,
} from 'lucide-react';

const STATUS_LABELS = {
  submitted: 'Demande reçue',
  account_created: 'Compte créé',
  dossier_in_progress: 'Dossier en cours',
  school_review: 'Validation école',
  funder_review: 'Validation financeur',
  payment_pending: 'En attente de règlement',
  confirmed: 'Inscription validée ✓',
  rejected: 'Refusée',
};

const STEPS = [
  { key: 'submitted',           label: 'Demande' },
  { key: 'account_created',     label: 'Compte créé' },
  { key: 'dossier_in_progress', label: 'Dossier' },
  { key: 'school_review',       label: 'Validation école' },
  { key: 'funder_review',       label: 'Financeur' },
  { key: 'payment_pending',     label: 'Règlement' },
  { key: 'confirmed',           label: 'Validée' },
];

export const MyDossierPage = () => {
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [my, reqs] = await Promise.all([
        api.myDossier(),
        api.getDossierRequirements(),
      ]);
      setDossiers(my || []);
      setRequirements(reqs.requirements || []);
    } catch (_) { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F0] pt-32 pb-20 px-6" data-testid="my-dossier-page">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3">MON ESPACE</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#1F2A4A] font-light mb-2">
          Mon <span className="italic text-[#C9B896]">dossier.</span>
        </h1>
        <p className="text-sm text-[#1F2A4A]/60 mb-12">
          Suis l'avancée de ton inscription et complète les pièces requises.
        </p>

        {loading ? (
          <div className="text-center py-20 text-[#1F2A4A]/40">Chargement…</div>
        ) : dossiers.length === 0 ? (
          <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-16 text-center">
            <FileText className="w-12 h-12 text-[#C9B896] mx-auto mb-4 opacity-50" />
            <h2 className="font-serif text-xl text-[#1F2A4A] mb-2">Aucune candidature en cours</h2>
            <p className="text-sm text-[#1F2A4A]/60 mb-6">Tu n'as pas encore déposé de demande de formation.</p>
            <Button onClick={() => (window.location.href = '/fr/formations-sur-place')} className="bg-[#C9B896] text-[#1F2A4A] rounded-full">
              Voir les formations
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {dossiers.map((app) => (
              <DossierCard key={app.id} app={app} requirements={requirements} onUpdated={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DossierCard = ({ app, requirements, onUpdated }) => {
  const stepIdx = STEPS.findIndex((s) => s.key === app.status);

  return (
    <article className="bg-white border border-[#1F2A4A]/10 rounded-2xl overflow-hidden shadow-sm" data-testid={`dossier-card-${app.id}`}>
      <div className="p-6 md:p-8 border-b border-[#1F2A4A]/10 bg-gradient-to-br from-white to-[#FAF7F0]/40">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">CANDIDATURE</p>
            <h2 className="font-serif text-2xl text-[#1F2A4A]">{app.course?.title}</h2>
            {app.session?.start_date && (
              <p className="text-sm text-[#1F2A4A]/60 mt-1">
                Session du {new Date(app.session.start_date).toLocaleDateString('fr-FR')}
              </p>
            )}
          </div>
          <span className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full font-medium ${
            app.status === 'confirmed' ? 'bg-emerald-500/15 text-emerald-700' :
            app.status === 'rejected' ? 'bg-red-500/10 text-red-600' :
            'bg-amber-500/15 text-amber-700'
          }`} data-testid={`dossier-status-${app.id}`}>
            {STATUS_LABELS[app.status] || app.status}
          </span>
        </div>

        {/* Pipeline */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center shrink-0">
              <div className={`flex flex-col items-center min-w-[80px]`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                  i < stepIdx ? 'bg-emerald-500/15 text-emerald-700' :
                  i === stepIdx ? 'bg-[#C9B896] text-[#1F2A4A] ring-4 ring-[#C9B896]/20' :
                  'bg-[#1F2A4A]/8 text-[#1F2A4A]/30'
                }`}>
                  {i < stepIdx ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <p className={`text-[10px] mt-1.5 text-center font-medium uppercase tracking-wider ${
                  i === stepIdx ? 'text-[#1F2A4A]' : 'text-[#1F2A4A]/40'
                }`}>{s.label}</p>
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-[#1F2A4A]/20 -mt-4" />}
            </div>
          ))}
        </div>
      </div>

      {/* Body — content depends on status */}
      <div className="p-6 md:p-8">
        {app.status === 'submitted' && (
          <p className="text-sm text-[#1F2A4A]/70 italic">
            <Mail className="w-4 h-4 inline text-[#C9B896] mr-1" /> Ta demande est enregistrée. L'école va te contacter pour la suite.
          </p>
        )}

        {(['dossier_in_progress', 'school_review', 'funder_review', 'payment_pending'].includes(app.status)) && (
          <DocumentsList app={app} requirements={requirements} onUpdated={onUpdated} />
        )}

        {app.status === 'payment_pending' && (
          <div className="mt-6 p-5 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <p className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-1">
              <CreditCard className="w-4 h-4" /> Règlement attendu
            </p>
            <p className="text-xs text-amber-700/80">
              L'école va te contacter pour finaliser le règlement (Stripe, PayPal, virement OPCO ou financement privé).
            </p>
          </div>
        )}

        {app.status === 'confirmed' && (
          <div className="p-6 bg-emerald-500/8 border border-emerald-500/20 rounded-xl text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <p className="font-serif text-xl text-emerald-800 mb-1">Bienvenue dans la promotion !</p>
            <p className="text-sm text-emerald-700/70">Tu recevras prochainement les détails pratiques par email.</p>
          </div>
        )}

        {app.status === 'rejected' && (
          <div className="p-5 bg-red-500/8 border border-red-500/20 rounded-xl">
            <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
            <p className="text-sm text-red-700">
              Ta candidature n'a pas été retenue cette fois-ci. N'hésite pas à <a href="/fr/contact" className="underline">nous contacter</a> pour en discuter.
            </p>
          </div>
        )}
      </div>
    </article>
  );
};

const DocumentsList = ({ app, requirements, onUpdated }) => {
  const fileInputs = useRef({});

  const upload = async (key, file) => {
    if (!file) return;
    try {
      toast.loading("Envoi du document…", { id: `upload-${key}` });
      await api.uploadDossierDocument(app.id, key, file);
      toast.success("Document téléversé", { id: `upload-${key}` });
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi", { id: `upload-${key}` });
    }
  };

  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3">PIÈCES À DÉPOSER</p>
      <ul className="space-y-2">
        {requirements.map((req) => {
          const doc = (app.documents || []).find((d) => d.key === req.key);
          const status = doc?.status || 'missing';
          return (
            <li key={req.key} className="flex items-center gap-3 p-4 bg-[#FAF7F0]/60 rounded-lg" data-testid={`my-doc-${req.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1F2A4A]">
                  {req.label} {req.required && <span className="text-red-500">*</span>}
                </div>
                {doc?.file_name && (
                  <p className="text-[11px] text-[#1F2A4A]/50 font-mono mt-0.5 truncate">{doc.file_name}</p>
                )}
                {doc?.rejection_reason && (
                  <p className="text-[11px] text-red-600 mt-1">⚠ Refusé : {doc.rejection_reason}</p>
                )}
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-medium shrink-0 ${
                status === 'validated' ? 'bg-emerald-500/15 text-emerald-700' :
                status === 'uploaded' ? 'bg-amber-500/15 text-amber-700' :
                status === 'rejected' ? 'bg-red-500/10 text-red-600' :
                'bg-[#1F2A4A]/8 text-[#1F2A4A]/50'
              }`}>
                {status === 'validated' ? '✓ Validé' :
                 status === 'uploaded' ? 'En attente' :
                 status === 'rejected' ? 'Refusé' : 'À déposer'}
              </span>
              <input
                ref={(el) => (fileInputs.current[req.key] = el)}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => upload(req.key, e.target.files?.[0])}
                data-testid={`upload-input-${req.key}`}
              />
              <button
                onClick={() => fileInputs.current[req.key]?.click()}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] transition-colors shrink-0"
                data-testid={`upload-btn-${req.key}`}
                disabled={status === 'validated'}
              >
                <Upload className="w-3 h-3" />
                {status === 'missing' ? 'Déposer' : 'Remplacer'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MyDossierPage;
